import NumberFlow from "@number-flow/react"
import { Flex, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"

import { useAnimatedNumber } from "../../hooks/useAnimatedNumber"
import Icon from "../../Icon"
import InfoTooltip from "../InfoTooltip/InfoTooltip"
import Private from "../Private"

import "./styles.less"

const { Title, Text } = Typography

// `masked` decides whether the figure is hidden in Incognito mode. Dashboard KPIs are sensitive, so
// it defaults to on; the Credits page opts out — its per-status request counts must stay readable
// (CHA1-1353).
const DashboardCard = ({ data, theme, masked = true, ...props }) => {
  const { t } = useTranslation()
  // A card can carry a label instead of a metric (e.g. the top country's name). Keep the hook
  // call unconditional and feed it a number, then pick the renderer below.
  const isText = typeof data?.value === "string"
  const animatedValue = useAnimatedNumber(isText ? 0 : data?.value)

  const format =
    data?.format ||
    (data?.prefix
      ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      : { maximumFractionDigits: 0 })

  return (
    <Flex vertical gap={4} className={cls("dashboard-card")}>
      <Flex
        gap={8}
        className="wrapper dashboard-card-content"
        vertical
        justify={data?.icon ? "flex-start" : "center"}
        style={{ height: "100%" }}
      >
        {data?.icon && (
          <span className="dashboard-card-icon">
            <Icon type={data.icon} />
          </span>
        )}
        <Flex gap={8} align="center">
          <Text ellipsis type="secondary" strong style={{ fontSize: 14 }}>
            {t(data?.title)}
          </Text>
          {data?.tooltip && <InfoTooltip title={t(`${data?.tooltip}`)} />}
          {/* Optional trailing icon, pinned top-right (e.g. the Home KPI's solution
              icon). The caller passes a rendered <Icon/> node. Distinct from `data.icon`,
              which renders a big icon at the top of the card from an icon-type string. */}
          {data?.trailingIcon ? (
            <span className="dashboard-card_icon" style={{ marginLeft: "auto", display: "flex" }}>
              {data.trailingIcon}
            </span>
          ) : null}
        </Flex>
        {/* When a card is gated (e.g. a Home solution that isn't activated yet) the
            caller passes a `cta` node, shown in place of the value as a minimal
            "Activate" affordance. */}
        {data?.cta ? (
          data.cta
        ) : (
          <Flex gap={8} align="baseline" justify="space-between">
            {data?.value == null ? (
              <Text style={{ fontSize: 24, lineHeight: 1.4 }}>-</Text>
            ) : isText ? (
              <Text strong ellipsis style={{ fontSize: 24, lineHeight: 1.4 }}>
                <Private disabled={!masked}>{data.value}</Private>
              </Text>
            ) : (
              <Text strong style={{ fontSize: 24, lineHeight: 1.4 }}>
                <Private disabled={!masked}>
                  <NumberFlow
                    value={animatedValue}
                    prefix={data?.prefix || ""}
                    suffix={data?.suffix || ""}
                    format={format}
                  />
                </Private>
              </Text>
            )}
            {/* Optional secondary value shown inline next to the headline (e.g. the
                Health "X% without us" comparison), matching the ChargebackOverview look.
                The caller passes an already-translated string. */}
            {data?.valueAside ? (
              <Text type="secondary" strong style={{ fontSize: 14 }}>
                {data.valueAside}
              </Text>
            ) : null}
          </Flex>
        )}
        {data?.comparison_value != null && (
          <Text type="secondary" style={{ margin: 0, maxWidth: "100%" }}>
            <Private disabled={!masked}>
              <NumberFlow
                value={Number(data.comparison_value) || 0}
                prefix={data?.prefix || ""}
                format={
                  data?.prefix
                    ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                    : { maximumFractionDigits: 0 }
                }
              />
            </Private>{" "}
            {data?.comparison_period?.label || t("previous period")}
          </Text>
        )}
        {data?.subtitle ? (
          <Text type="secondary" style={{ fontSize: 14 }}>
            {t(data.subtitle)}
          </Text>
        ) : null}
      </Flex>
    </Flex>
  )
}

export default DashboardCard
